import { useContext, useEffect } from "react";
import { useWatch } from "react-hook-form";
import { RecordContext } from "../../components/record/Record";
import { DynamicTasksGroupsLayout } from "./DynamicTasksGroupsLayout";
import { useUser } from "../../api/useAuth";
import { FieldExtraOption } from "../../types/fields.types";
import { DEPARTMENT_FIELD, getAllowedDepartments } from "./department-validation/departmentAccess";
import {
	allTriggerFields,
	dynamicTasksOptions,
	hasValue,
	hiddenChoicesByCollection,
	mergeFieldExtraOptions,
	triggerFieldsByCollection
} from "./dynamicTasksOptions";

export const DynamicTasksForm = () => {
	const context = useContext(RecordContext);
	const entityForm = context?.entityForm;
	const item = context?.item;
	const { data: user } = useUser();

	const allowedDepartments = getAllowedDepartments(user?.role?.id);
	const defaultDepartment = allowedDepartments.length === 1 ? allowedDepartments[0] : undefined;

	const watchedTargetCollection = useWatch({ control: entityForm?.control, name: "target_collection" });
	const targetCollection = watchedTargetCollection ?? item?.target_collection;
	const visibleTriggerFields = triggerFieldsByCollection[targetCollection] || [];
	const hiddenChoicesForCollection = hiddenChoicesByCollection[targetCollection] ?? {};

	useEffect(() => {
		if (!entityForm) return;
		allTriggerFields.forEach((fieldName) => {
			if (visibleTriggerFields.includes(fieldName)) return;
			if (hasValue(entityForm.getValues(fieldName))) {
				entityForm.setValue(fieldName, null, { shouldDirty: true });
			}
		});
		// A value that just became a hidden choice for this collection is no longer valid — clear it.
		Object.entries(hiddenChoicesForCollection).forEach(([fieldName, hiddenValues]) => {
			const currentValue = entityForm.getValues(fieldName);
			if (hasValue(currentValue) && hiddenValues.includes(currentValue)) {
				entityForm.setValue(fieldName, null, { shouldDirty: true });
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [targetCollection, entityForm]);

	useEffect(() => {
		if (!entityForm || context?.type !== "create") return;
		if (entityForm.getValues("due_date")) return;
		const defaultDueDate = new Date();
		defaultDueDate.setHours(18, 0, 0, 0);
		entityForm.setValue("due_date", defaultDueDate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entityForm, context?.type]);

	if (!context || !entityForm) return null;

	const baseGroupsOptions = dynamicTasksOptions.groupsOptions;
	const baseExtraOptions = baseGroupsOptions?.fieldsWithExtraOptions ?? [];

	const overridesByField: Record<string, FieldExtraOption> = {
		status: { defaultValue: "active" },
		[DEPARTMENT_FIELD]: { allowedChoices: allowedDepartments, defaultValue: defaultDepartment },
		// content is pulled out of its group and rendered at the end of the attribution column.
		content: { hidden: { create: true, update: true } },
		user_created: { hidden: { create: true } }
	};

	allTriggerFields.forEach((field) => {
		const isFieldHidden = !visibleTriggerFields.includes(field);
		overridesByField[field] = { hidden: { create: isFieldHidden, update: isFieldHidden } };
	});

	// Restrict select choices per collection: allow the field's Directus choices minus the hidden ones.
	Object.entries(hiddenChoicesForCollection).forEach(([fieldName, hiddenValues]) => {
		const fieldChoices =
			context.formatedFields?.find((field) => field.field === fieldName)?.settings?.options?.choices ?? [];
		const allowedChoices = fieldChoices
			.map((choice) => choice.value)
			.filter((choiceValue): choiceValue is string => choiceValue != null && !hiddenValues.includes(choiceValue));
		overridesByField[fieldName] = { ...overridesByField[fieldName], allowedChoices };
	});

	const dynamicGroupsOptions = {
		...baseGroupsOptions,
		fieldsWithExtraOptions: mergeFieldExtraOptions(baseExtraOptions, overridesByField)
	};

	return <DynamicTasksGroupsLayout groupsOptions={dynamicGroupsOptions} />;
};
